import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Settings, Save, Building2, Percent, Mail, Plus, X, Calendar, Send, RefreshCw } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanySettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTds, setSavingTds] = useState(false);
  const [sendingTestReport, setSendingTestReport] = useState(false);
  const [settings, setSettings] = useState({
    pan_number: "ABECA5341A",
    mobile_number: "",
    email: "",
    address: ""
  });
  
  // TDS Report Configuration
  const [tdsConfig, setTdsConfig] = useState({
    enabled: false,
    recipient_emails: [],
    send_day: 1
  });
  const [newTdsEmail, setNewTdsEmail] = useState("");

  useEffect(() => {
    fetchSettings();
    fetchTdsConfig();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/company-settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error("Failed to fetch company settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchTdsConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/tds-report/config`);
      setTdsConfig(response.data);
    } catch (error) {
      console.error("Failed to fetch TDS config:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/company-settings`, settings);
      toast.success("Company settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTdsConfig = async () => {
    setSavingTds(true);
    try {
      await axios.post(`${API_URL}/tds-report/config`, tdsConfig);
      toast.success("TDS report configuration saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save TDS configuration");
    } finally {
      setSavingTds(false);
    }
  };

  const addTdsEmail = () => {
    if (!newTdsEmail) return;
    if (!newTdsEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (tdsConfig.recipient_emails.includes(newTdsEmail)) {
      toast.error("Email already added");
      return;
    }
    setTdsConfig({
      ...tdsConfig,
      recipient_emails: [...tdsConfig.recipient_emails, newTdsEmail]
    });
    setNewTdsEmail("");
  };

  const removeTdsEmail = (email) => {
    setTdsConfig({
      ...tdsConfig,
      recipient_emails: tdsConfig.recipient_emails.filter(e => e !== email)
    });
  };

  const sendTestReport = async () => {
    if (tdsConfig.recipient_emails.length === 0) {
      toast.error("Please add at least one recipient email first");
      return;
    }
    setSendingTestReport(true);
    try {
      const response = await axios.post(`${API_URL}/tds-report/send-monthly`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test report");
    } finally {
      setSendingTestReport(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="company-settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
          <Settings className="w-8 h-8 text-primary" />
          Company Settings
        </h1>
        <p className="text-muted-foreground mt-1">Configure company details for load slips and invoices</p>
      </div>

      {/* Settings Card */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Ardhanageshwar Logistics Pvt. Ltd.
          </CardTitle>
          <CardDescription>
            These details will appear on Load Slips, Invoices, and other documents
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pan_number">PAN Number</Label>
              <Input
                id="pan_number"
                value={settings.pan_number}
                onChange={(e) => setSettings({ ...settings, pan_number: e.target.value.toUpperCase() })}
                className="bg-secondary/50 border-border font-mono"
                placeholder="ABECA5341A"
                maxLength={10}
                data-testid="pan-number-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="mobile_number">Mobile Number</Label>
              <Input
                id="mobile_number"
                value={settings.mobile_number || ""}
                onChange={(e) => setSettings({ ...settings, mobile_number: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="9876543210"
                maxLength={15}
                data-testid="mobile-number-input"
              />
              <p className="text-xs text-muted-foreground">Will appear on Load Slips</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={settings.email || ""}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="info@ardhanageshwar.com"
                data-testid="email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={settings.address || ""}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              className="bg-secondary/50 border-border"
              placeholder="Company registered address"
              data-testid="address-input"
            />
          </div>

          <div className="flex justify-end pt-4 border-t border-border">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="glow-primary bg-primary"
              data-testid="save-settings-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* TDS Report Configuration Card */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading flex items-center gap-2">
            <Percent className="w-5 h-5 text-purple-400" />
            Automated TDS Reports
          </CardTitle>
          <CardDescription>
            Configure automatic monthly TDS report emails
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
            <div>
              <Label className="text-base font-semibold">Enable Automated Reports</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Automatically send TDS summary email on the selected day each month
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={tdsConfig.enabled}
                onChange={(e) => setTdsConfig({ ...tdsConfig, enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            </label>
          </div>

          {/* Send Day Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                Send Report On Day
              </Label>
              <Select
                value={tdsConfig.send_day?.toString()}
                onValueChange={(v) => setTdsConfig({ ...tdsConfig, send_day: parseInt(v) })}
              >
                <SelectTrigger className="bg-secondary/50">
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(28)].map((_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {i + 1}{i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'} of every month
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Report for previous month will be sent on this day
              </p>
            </div>
            <div className="space-y-2">
              <Label>Report Type</Label>
              <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <p className="font-medium text-purple-400">Monthly TDS Summary</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Includes TDS receivable, payable, net liability, and breakdown by rate
                </p>
              </div>
            </div>
          </div>

          {/* Recipient Emails */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              Recipient Email Addresses
            </Label>
            
            {/* Email List */}
            <div className="flex flex-wrap gap-2 min-h-[40px] p-3 bg-secondary/30 rounded-lg">
              {tdsConfig.recipient_emails.length === 0 ? (
                <p className="text-sm text-muted-foreground">No recipients added yet</p>
              ) : (
                tdsConfig.recipient_emails.map((email) => (
                  <Badge 
                    key={email} 
                    className="bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 px-3 py-1 flex items-center gap-2"
                  >
                    {email}
                    <button
                      onClick={() => removeTdsEmail(email)}
                      className="hover:text-red-400 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))
              )}
            </div>

            {/* Add Email Input */}
            <div className="flex gap-2">
              <Input
                type="email"
                value={newTdsEmail}
                onChange={(e) => setNewTdsEmail(e.target.value)}
                placeholder="Enter email address"
                className="bg-secondary/50"
                onKeyPress={(e) => e.key === 'Enter' && addTdsEmail()}
              />
              <Button onClick={addTdsEmail} variant="outline">
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t border-border">
            <Button 
              variant="outline" 
              onClick={sendTestReport}
              disabled={sendingTestReport || tdsConfig.recipient_emails.length === 0}
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
            >
              {sendingTestReport ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test Report (Last Month)
                </>
              )}
            </Button>
            <Button 
              onClick={handleSaveTdsConfig} 
              disabled={savingTds}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingTds ? "Saving..." : "Save TDS Configuration"}
            </Button>
          </div>

          {/* Status Info */}
          {tdsConfig.enabled && tdsConfig.recipient_emails.length > 0 && (
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-sm text-green-400 font-medium">
                Automated reports are enabled
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Monthly TDS report will be sent to {tdsConfig.recipient_emails.length} recipient(s) 
                on the {tdsConfig.send_day}{tdsConfig.send_day === 1 ? 'st' : tdsConfig.send_day === 2 ? 'nd' : tdsConfig.send_day === 3 ? 'rd' : 'th'} of every month.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanySettingsPage;
